import { useCallback, useEffect } from 'react';
import { Button, Card, Form, Input, Row, Space } from 'antd';
import { observer } from 'mobx-react-lite';

import { useResource } from 'app/common/api';
import { requiredRule } from 'app/common/form-helpers/features/rules';
import { NotificationsRender } from 'app/common/notifications/features/notifications';
import { usePageNotify } from 'app/common/notifications/features/page-notifications';
import { Loading } from 'app/ui';

import { TPermission } from '../../../core/permissions';
import { TRole } from '../../../core/role';
import { AvailableRolePermissionsTransfer } from '../../../modules/available-role-permissions-transfer/available-role-permissions-transfer';
import { getRoleRequest } from '../../../requests/get-role.request';
import { updateRoleRequest } from '../../../requests/update-role.request';
import { CanAccess } from '../../permissions';

type RoleFormFields = {
  title: string;
  name: string;
  permissions: TPermission[];
};

type UpdateRoleFeatureProps = { roleId: number };

export const UpdateRoleFeature = observer(
  ({ roleId }: UpdateRoleFeatureProps) => {
    const notify = usePageNotify();
    const [form] = Form.useForm();

    const getRoleResource = useResource<number, TRole>({
      request: getRoleRequest,
      errorNotify: notify,
      onDone(role) {
        form.setFieldsValue(role);
      },
    });

    useEffect(() => {
      getRoleResource.send(roleId);
    }, [roleId]);

    const updateRoleResource = useResource<TRole, TRole>({
      request: updateRoleRequest,
      errorNotify: notify,
      onDone(updatedRole) {
        form.setFieldsValue(updatedRole);

        notify.addMessage({
          type: 'success',
          message: 'Данные роли были обновлены',
        });
      },
    });

    const handleFormSubmit = useCallback(
      (role: RoleFormFields) => {
        updateRoleResource.send({ ...role, id: roleId });
      },
      [roleId, updateRoleResource],
    );

    const isLoading = getRoleResource.isLoading || updateRoleResource.isLoading;

    return (
      <>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          scrollToFirstError={{ behavior: 'smooth' }}
        >
          <Loading active={isLoading}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <Card size="small" title="Данные роли">
                <Form.Item
                  name="title"
                  label="Название"
                  rules={[requiredRule()]}
                  style={{ maxWidth: '50%' }}
                >
                  <Input />
                </Form.Item>

                <Form.Item
                  name="name"
                  label="Идентификатор"
                  rules={[requiredRule()]}
                  style={{ maxWidth: '50%' }}
                >
                  <Input />
                </Form.Item>
              </Card>

              <Card size="small" title="Разрешения для роли">
                <Form.Item name="permissions" label="" rules={[requiredRule()]}>
                  <AvailableRolePermissionsTransfer />
                </Form.Item>
              </Card>

              <NotificationsRender notifyStore={notify} />

              <CanAccess perm="PERM_USER_ROLE_UPDATE">
                <Card size="small">
                  <Row>
                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{ marginLeft: 'auto' }}
                    >
                      Сохранить
                    </Button>
                  </Row>
                </Card>
              </CanAccess>
            </Space>
          </Loading>
        </Form>
      </>
    );
  },
);
